import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type ToastTone = 'success' | 'error' | 'info';

interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
}

interface ToastRecord extends Required<Pick<ToastInput, 'title'>> {
  id: string;
  description?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  toast: (input: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function createToastId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function toneClasses(tone: ToastTone): { accent: string; title: string } {
  switch (tone) {
    case 'error':
      return { accent: 'bg-rose-500', title: 'text-rose-700' };
    case 'info':
      return { accent: 'bg-sky-500', title: 'text-sky-700' };
    default:
      return { accent: 'bg-emerald-500', title: 'text-emerald-700' };
  }
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timeoutMapRef = useRef(new Map<string, number>());

  const dismissToast = useCallback((id: string) => {
    const timeoutId = timeoutMapRef.current.get(id);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutMapRef.current.delete(id);
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback((input: ToastInput) => {
    const id = createToastId();
    const record: ToastRecord = {
      id,
      title: input.title,
      description: input.description,
      tone: input.tone ?? 'info',
    };

    setToasts((current) => [...current, record]);

    const timeoutId = window.setTimeout(() => {
      setToasts((current) => current.filter((toastItem) => toastItem.id !== id));
      timeoutMapRef.current.delete(id);
    }, 2800);

    timeoutMapRef.current.set(id, timeoutId);
  }, []);

  useEffect(() => {
    return () => {
      timeoutMapRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutMapRef.current.clear();
    };
  }, []);

  const contextValue = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
          <div
            aria-live="polite"
            aria-relevant="additions removals"
            className="fixed bottom-4 right-4 z-[80] flex w-[min(92vw,22rem)] flex-col gap-3"
            data-ai-id="toast-container"
          >
            {toasts.map((toastItem) => {
              const colors = toneClasses(toastItem.tone);

              return (
                <div
                  key={toastItem.id}
                  role="status"
                  className="overflow-hidden border border-slate-300 bg-white"
                  data-ai-id="toast-item"
                >
                  <div className="flex" data-ai-id="toast-item-row">
                    <div className={['w-1.5 shrink-0', colors.accent].join(' ')} data-ai-id="toast-item-accent" />
                    <div className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3" data-ai-id="toast-item-content">
                      <div className="min-w-0 flex-1" data-ai-id="toast-item-copy">
                        <p className={['text-sm font-semibold', colors.title].join(' ')} data-ai-id="toast-item-title">{toastItem.title}</p>
                        {toastItem.description ? (
                          <p className="mt-1 text-sm leading-5 text-slate-500" data-ai-id="toast-item-description">{toastItem.description}</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        aria-label="Close notification"
                        className="p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                        onClick={() => dismissToast(toastItem.id)}
                        data-ai-id="toast-item-close-button"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>,
          document.body,
        )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }

  return context;
}
